import { NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import type { CarouselFeature } from '@indutrust/shared-model';
import { provideTranslocoScopeSharedFeaturesCarousel } from '@indutrust/shared-util';
import { TranslocoDirective } from '@jsverse/transloco';
import { Tag } from 'primeng/tag';

@Component({
  selector: 'idt-carousel-feature-card',
  imports: [NgOptimizedImage, NgTemplateOutlet, Tag, TranslocoDirective],
  template: `
    <ng-container *transloco="let t; prefix: 'sharedFc'">
      <div class="card-surface overflow-hidden w-full rounded-xl shadow-md">
        @if (feature().coverType === 'image') {
          <ng-container *ngTemplateOutlet="coverImageTemplate" />
        } @else {
          <ng-container *ngTemplateOutlet="coverVisionTemplate" />
        }

        <div class="p-6">
          <h3 class="m-0 mb-2 text-color">{{ t(feature().title) }}</h3>
          <p class="text-muted-color leading-relaxed m-0">
            {{ t(feature().description) }}
          </p>
          <div class="flex flex-wrap gap-2 mt-4">
            @for (tag of feature().tags; track tag) {
              <p-tag
                [pt]="{
                  root: { style: { backgroundColor: tagColors[$index] } },
                }"
                [value]="
                  t('features.' + feature().id + '.tags.' + ($index + 1))
                " />
            }
          </div>
        </div>
      </div>

      <ng-template #coverImageTemplate>
        @if (feature().coverImage; as featureCoverImage) {
          <div class="feature-image-container">
            @if (feature().id === 'decisionMaking') {
              <img
                priority
                [alt]="t(feature().title)"
                [height]="260"
                [ngSrc]="featureCoverImage"
                [width]="600" />
            } @else {
              <img
                loading="lazy"
                [alt]="t(feature().title)"
                [height]="260"
                [ngSrc]="featureCoverImage"
                [width]="600" />
            }
          </div>
        }
      </ng-template>

      <ng-template #coverVisionTemplate>
        <div
          class="cover-visions-container flex flex-wrap items-center justify-around gap-4 p-4 border-b border-surface">
          @for (vision of feature().coverVisions; track $index) {
            <div class="logo-container">
              <img
                loading="lazy"
                [alt]="t(feature().title)"
                [height]="50"
                [ngSrc]="vision"
                [width]="135" />
            </div>
          }
        </div>
      </ng-template>
    </ng-container>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
      padding: 0.5rem;
      box-sizing: border-box;
    }

    /* Mirrors p-card theming so the card adapts to light/dark automatically */
    .card-surface {
      background: var(--p-content-background);
      color: var(--p-content-color);
    }

    /* Only styles that cannot be expressed with Tailwind utilities */
    .feature-image-container {
      width: 100%;

      img {
        display: block;
        width: 100%;
        height: auto;
      }
    }

    .logo-container {
      // width: 8.4375rem;
      // max-width: calc(33.333% - 1rem);
      width: 12rem;
      max-width: 75%;
      border-radius: 0.5rem;
      padding: 0.5rem;

      img {
        width: 100%;
        height: auto;
        object-fit: contain;
      }
    }

    /* In dark mode, use a surface grey from the palette instead of bright white */
    :host-context(.app-dark) .cover-visions-container,
    :host-context(.app-dark) .logo-container {
      background-color: var(--p-content-hover-background);
    }

    p-tag {
      color: var(--p-tag-contrast-color);
    }
  `,
  providers: [provideTranslocoScopeSharedFeaturesCarousel()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 * Card component for a single feature in the features carousel.
 *
 * Renders a cover image or a grid of partner visions and displays localized
 * title, description and tags based on the provided {@link CarouselFeature}.
 *
 * @example
 * <idt-carousel-feature-card [feature]="feature" />
 */
export class CarouselFeatureCardComponent {
  readonly feature = input.required<CarouselFeature>();

  // Colors harmonized with the Indutrust brand blue (#1fa8f5):
  // - #0b8de0: darker brand blue (shade from the primary palette)
  // - #0eb5a0: teal (analogous cool accent)
  // - #7c5cfc: violet (split-complementary, modern contrast)
  // - #f5a01f: amber (direct complement to blue, warm accent)
  readonly tagColors = ['#0b8de0', '#0eb5a0', '#7c5cfc', '#f5a01f'];
}
